<template>
  <div class="pt3">
    <b-tabs
      position="is-centered"
      class="block importer-tabs"
      :animated="true"
      size="is-large"
    >
      <b-tab-item>
        <template slot="header">
          <nice-icon type="fab" icon="reddit" class="mr2"></nice-icon>
          <span>Reddit</span>
        </template>

        <page-container>
          <b-message v-if="!$user.meta.reddit">
            Here you can import saved posts, comments and links from reddit.
            <br />You need to connect your reddit account to use this feature.
            <br />
            <a
              class="button is-primary mt3"
              :href="
                `https://www.reddit.com/api/v1/authorize.compact?client_id=64xlRHVmY3l8NA&state=${$utils.randomString(
                  30
                )}&redirect_uri=${
                  $env.VUE_APP_URL
                }authorize/reddit&duration=permanent&scope=identity,history&response_type=code`
              "
              >Connect reddit account</a
            >
          </b-message>

          <div v-else>
            <b-message>
              <div class="flex-ns justify-between items-start">
                <div>
                  <p class="f5 mb3">Connected Reddit account:</p>
                  <a
                    class="inline-flex items-center"
                    style="text-decoration:none;"
                    :href="`https://reddit.com/u/${$user.meta.reddit.name}`"
                    target="_blank"
                  >
                    <div class="image is-48x48 mr2">
                      <img class="is-rounded" :src="$user.meta.reddit.avatar" />
                    </div>
                    <p class="f4 b no-underline">
                      {{ $user.meta.reddit.name }}
                    </p>
                  </a>
                </div>
                <div class="flex justify-end">
                  <b-button
                    type="is-danger"
                    size="is-small"
                    class="mt3 mt0-m"
                    @click="redditDisconnectDialog"
                    :loading="$wait.is('disconnecting reddit-account')"
                  >
                    <nice-icon icon="times" spacing-right></nice-icon>
                    <span>Disconnect</span>
                  </b-button>
                </div>
              </div>
            </b-message>

            <heading size="large" class="mt5">Saved on reddit</heading>

            <b-button
              v-if="redditPostsEmpty"
              @click="loadRedditPosts()"
              type="is-primary"
              class="is-fullwidth"
              :loading="$wait.is('loading.reddit-posts')"
              >Load saved posts</b-button
            >

            <reddit-item
              v-for="(post, index) in redditPosts"
              :key="index"
              :post="post"
              @save="openSave"
            ></reddit-item>
          </div>

          <div
            class="flex justify-center items-center mt4"
            v-if="!redditPostsEmpty"
          >
            <b-button
              @click="loadRedditPosts"
              :loading="$wait.is('loading reddit posts')"
              >Load more</b-button
            >
          </div>
        </page-container>
      </b-tab-item>
      <b-tab-item>
        <template slot="header">
          <nice-icon type="far" icon="layer-group" class="mr2"></nice-icon>
          <span>Bookmarks</span>
        </template>
        <page-container>
          <b-message>
            The feature to import Bookmarks from your Browser is coming soon.
            <br />Contact us if you have suggestions or questions.
          </b-message>
        </page-container>
      </b-tab-item>
      <b-tab-item>
        <template slot="header">
          <nice-icon type="far" icon="layer-group" class="mr2"></nice-icon>
          <span>Batch</span>
        </template>
        <page-container>
          <b-message>
            The feature to import multiple links at once is coming soon.
            <br />Contact us if you have suggestions.
          </b-message>
        </page-container>
      </b-tab-item>
    </b-tabs>

    <drawer
      position="right"
      :is-open.sync="isSavePostOpen"
      :content-style="{ width: '500px', overflowY: 'auto', zIndex: 40 }"
      :cover-content="true"
    >
      <add-resource
        :resource-data="currentResource"
        slot="content"
        is-in-overlay
        @saved="isSavePostOpen = false"
        @cancel="isSavePostOpen = false"
      ></add-resource>
    </drawer>
  </div>
</template>

<script>
import getUrls from "get-urls";
import AddResource from "@/views/AddResource.vue";
import RedditItem from "@/components/importer/reddit/Item.vue";

export default {
  components: {
    AddResource,
    RedditItem
  },

  data: () => ({
    redditPosts: [],
    isSavePostOpen: false,
    currentPost: null,
    currentResource: null
  }),

  methods: {
    async loadRedditPosts() {
      const reqData = {};
      if (this.redditPosts.length > 0) {
        const lastPost = this.redditPosts[this.redditPosts.length - 1];
        reqData.afterId = `${lastPost.kind}_${lastPost.data.id}`;
      }
      const posts = await this.$store.dispatch("getSavedRedditPosts", reqData);

      if (!posts) return;

      for (const post of posts.data.children) {
        post._urls = [];

        const text = post.data.selftext || post.data.body;
        if (text) {
          /**
           * Find all URLs
           */
          let urls = Array.from(getUrls(text));

          /**
           * Find only URLs that are set with the Markdown Editor
           */
          // urls = text.match(/\(http(.*)\)/g) || [];
          if (urls && urls.length > 0) {
            urls = urls.map(s =>
              s.replace(/\]\((.*?)\)/g, "").replace(/\(|\)/g, "")
            );
          }

          post._urls.push(...urls);
        }
        this.redditPosts.push(post);
      }
    },

    redditDisconnectDialog() {
      this.$buefy.dialog.confirm({
        title: "Disconnect account",
        message: "Are you sure you want to disconnect this reddit account?",
        type: "is-warning",
        hasIcon: true,
        animation: "pop-modal",
        onConfirm: async () => {
          this.$wait.start("disconnecting reddit-account");
          const success = await this.$store.dispatch("disconnectRedditAccount");
          this.$auth.fetch({
            success: () => {
              this.$wait.end("disconnecting reddit-account");
              if (success) {
                this.$buefy.toast.open({
                  message: `Reddit account successfully disconnected`,
                  type: "is-success"
                });
              }
            },
            error: () => {
              this.$wait.end("disconnecting reddit-account");
              window.location.reload();
            }
          });
        }
      });
    },

    openSave({ post = null, url = null }) {
      this.isSavePostOpen = true;
      this.$set(this, "currentPost", this.$utils.cloneDeep(post));
      const resource = {
        name: post ? post.data.title : null,
        url:
          url ||
          (post.data.permalink
            ? `https://reddit.com${post.data.permalink}`
            : post.data.url)
      };
      this.$set(this, "currentResource", resource);
    }
  },

  computed: {
    redditPostsEmpty() {
      return this.redditPosts.length < 1;
    }
  }
};
</script>

<style lang="scss">
.importer-tabs {
  .tab-content {
    padding: 2rem 0 !important;
  }
}
</style>
